import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SessionStorageService } from '../../services/session-storage.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { UnifiedFlowService } from '../unified.service';
import { MatDialog } from '@angular/material/dialog';
import { WarnDialogComponent } from '../../warn-dialog/warn-dialog.component';
import { Subject } from 'rxjs';
import { ModuleStatusService } from '../../services/module-status.service';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-left-nav',
  templateUrl: './left-nav.component.html',
  styleUrls: ['./left-nav.component.scss'],
  imports: [CommonModule, MatButtonModule, MatIconModule],
})
export class LeftNavComponent implements OnInit, OnChanges, OnDestroy {
  private router = inject(Router);
  private ss = inject(SessionStorageService);
  unfFlowSvc = inject(UnifiedFlowService);
  modStatusSvc = inject(ModuleStatusService);
  private route = inject(ActivatedRoute);
  private dialog = inject(MatDialog);

  @Input() searchPage = false;
  @Input() stepInput;
  @Input() currentApp = 'rb';
  @Input() iwValidateTrack: boolean;
  @Input() iwProg;
  @Input() selectedIWProg;
  @Input() liValidateTrack: boolean;
  @Input() modules;
  @Input() loader = false;
  @Output() tabChanged = new EventEmitter();
  @Output() appChanged = new EventEmitter();
  @Output() appPage = new EventEmitter();
  @Output() checkStatuses = new EventEmitter();

  isNaN: (value: unknown) => boolean = Number.isNaN;

  rights = [];
  currentTab: any = 0;
  clientInfo = false;
  state;
  visitedSteps = [];
  stepStatus = [];
  profile;
  stepsObs;
  stepsList = [];
  notesValid;
  disableButtons = false;
  apps = [];
  appStatuses = this.modStatusSvc.appStatuses();
  hasAwComparisonPage = this.modStatusSvc.hasAwComparisonPage();
  unsubscribe = new Subject();
  fundAllocationVisited: boolean = false;

  ngOnInit(): void {
    //
    // Quick and Dirty refactor to get left nav working
    // Sorry for butchering your code Shawn
    //
    this.route.params.subscribe(params => {
      this.profile = params.id;
    });
    this.rights = this.ss.get('rights');
    this.clientInfo = this.ss.get('currentApp') === 'rb';

    const storageApp = this.ss.get('currentApp');

    this.currentApp =
      storageApp && !this.currentApp ? storageApp : this.currentApp;
    this.currentTab = this.stepInput;

    if (
      this.currentApp === 'iw' &&
      (this.currentTab === 'fund-allocation' ||
        this.currentTab === 'advisory' ||
        this.currentTab === 'results' ||
        this.currentTab === 'validate') &&
      !this.fundAllocationVisited
    ) {
      this.fundAllocationVisited = true;
    }

    if (!this.stepsObs) {
      this.stepsObs = this.unfFlowSvc.stepsListExternal;
      this.stepsObs
        .pipe(debounceTime(250), distinctUntilChanged())
        .subscribe(x => {
          this.stepsList = [...x];
        });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.appInput) {
      if (changes.appInput.currentValue !== changes.appInput.previousValue) {
        this.changeApp(changes.appInput.currentValue, true);
      }
    }
    if (changes.stepInput) {
      if (changes.stepInput.currentValue !== changes.stepInput.previousValue) {
        this.changeTab(null, changes.stepInput.currentValue, true);
      }

      if (changes.stepInput.previousValue === 'results') {
        const app = changes.currentApp
          ? changes.currentApp.currentValue
          : this.currentApp;
        const validNoteTrigger = changes.currentApp ? false : true;
        this.changeApp(app, true, validNoteTrigger);
      }
    }
  }

  ngOnDestroy() {
    this.unsubscribe.next(null);
    this.unsubscribe.complete();
    this.profile = null;
  }

  changeTab(step, index, external = false) {
    this.currentTab = index;
    if (
      this.currentApp === 'iw' &&
      (this.currentTab === 'fund-allocation' ||
        this.currentTab === 'advisory' ||
        this.currentTab === 'results' ||
        this.currentTab === 'validate') &&
      !this.fundAllocationVisited
    ) {
      this.fundAllocationVisited = true;
    }

    if (!external) {
      this.unfFlowSvc.setTriggerTabChange({
        step: step,
        index: index,
        fromNav: true,
      });
    }
  }

  changeApp(app, external = false, changeApp?) {
    const notesActive =
      this.rights.includes('BridgeNotes') &&
      this.isNaN(+this.currentTab) &&
      (this.currentTab === 'validate' ||
        this.currentTab === 'results' ||
        this.currentTab === 'products');
    this.notesValid =
      changeApp || this.unfFlowSvc.getNoteValidity(this.currentApp);

    if (
      this.currentApp !== 'summary' &&
      !this.isNaN(+this.currentTab) &&
      !external
    ) {
      this.unfFlowSvc.setTriggerSave({
        msg: 'appChangeSave-' + this.currentApp,
        id: this.profile,
        appChange: app,
        fromNav: true,
      });
    }

    if (notesActive) {
      if (
        !this.notesValid &&
        this.notesValid !== undefined &&
        this.rights.includes('BridgeNotes')
      ) {
        let dialogRef = this.dialog.open(WarnDialogComponent, {
          minWidth: '35vw',
          panelClass: 'warn-dialog',
          data: {
            headline: 'Warning',
            content:
              '<div class="warn-text">Please enter rationale on all required items (as indicated by the "Notes Required for This Item" flag).<br/>Each required note needs to be filled out and <u>saved</u> in order to continue.</div>',
            confirm: 'OK',
            hideCancel: true,
          },
        });
        dialogRef.afterClosed().subscribe(() => {
          dialogRef = null;
        });
        return false;
      }
    }

    if (this.currentApp !== app) {
      this.clientInfo = this.currentApp === 'rb';
      if (
        !external &&
        !(
          this.currentApp !== 'summary' &&
          !this.isNaN(+this.currentTab) &&
          !external
        )
      ) {
        this.unfFlowSvc.setTriggerAppChange({ app: app, fromNav: true });
      }
    } else if (!external) {
      if (this.currentApp !== app) {
        this.unfFlowSvc.setTriggerTabChange({
          step: null,
          index: 0,
          fromNav: true,
        });
      }
    }
  }

  changeToPage(app, tab) {
    if (app !== this.currentApp || tab !== this.currentTab) {
      this.appPage.emit({ app: app, tab: tab, fromNav: true });
    }
  }

  checkAppStep(i) {
    if (+this.currentTab === i) {
      return 'current step-button';
    } else if (
      (this.stepsList[i].visited && !this.stepsList[i].valid) ||
      (+this.currentTab < i &&
        this.stepsList[i + 1]?.visited &&
        !this.stepsList[i].valid) ||
      (+this.currentTab > i && !this.stepsList[i].valid)
    ) {
      return 'error step-button';
    } else if (
      isNaN(+this.currentTab) ||
      this.stepsList[i].visited ||
      (this.currentTab > i && this.stepsList[i].valid) ||
      (this.stepsList[i].valid && this.stepsList[i].completed)
    ) {
      return 'visited step-button';
    }
    return 'step-button';
  }

  checkFundAllocation() {
    return (
      this.fundAllocationVisited && this.unfFlowSvc.isFundAllocationMissing()
    );
  }

  goToHome() {
    this.disableButtons = true;
    this.router.navigate(['']);
  }
}
